// The feedback form's fields, shared by the public page (pages/SubmitFeedback.jsx)
// and a member of staff's own feedback (components/StaffFeedbackDialog.jsx), so the
// two ask the same questions in the same order and words.
//
// `mode="public"` asks everything. `mode="staff"` shows who, how to reach them, the
// event and their shift as fixed text from `fixed` — the server takes those from
// their record, whatever is sent. The form state's shape and every rule about it are
// feedbackRules.js; this file only lays it out.
//
// Rendered outside the app's providers on the public page, so it must not use
// MeContext, the app's axios or the Notifier.
import { useMemo } from "react";
import {
  Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, InputLabel,
  MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography,
} from "@mui/material";
import {
  FEEDBACK_LOCATION_CHOICES, FEEDBACK_NATURES, STAFF_FEEDBACK_NATURES, locationSummaryLabel,
  sortByLocationType,
} from "../constants";
import { OTHER_EVENT } from "../feedbackRules";

// What each kind of feedback is for, shown under the tick boxes in their order.
const NATURE_HINTS = {
  "Complaint": "is for formal dissatisfaction with how you have been treated.",
  "Speaking Up": "is for anything that could put patients, staff or the public at risk, "
    + "or that you think is wrong.",
  "Feedback": "is what you think we should start, stop or continue doing.",
  "Praise": "is just to say you appreciate what we do and how we do it.",
};

// A numbered question, as the paper version of a form would put it — it tells a
// member of the public how far through they are without a progress bar.
const Question = ({ n, title, hint, children }) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{n}. {title}</Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{hint}</Typography>
    )}
    <Stack spacing={1.5} sx={{ mt: hint ? 0 : 1 }}>{children}</Stack>
  </Box>
);

// A value the form doesn't ask for, because it's already known.
const Fixed = ({ value, empty = "—" }) => (
  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", color: value ? "text.primary" : "text.secondary" }}>
    {value || empty}
  </Typography>
);

export default function FeedbackForm({ form, setForm, mode = "public", events = [], fixed = {},
                                       showErrors = false }) {
  const staff = mode === "staff";
  const natures = staff ? STAFF_FEEDBACK_NATURES : FEEDBACK_NATURES;
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const toggleNature = (n) => setForm(f => ({
    ...f,
    nature: f.nature.includes(n) ? f.nature.filter(x => x !== n)
      // Kept in the list's own order whatever order they're ticked in.
      : natures.filter(x => x === n || f.nature.includes(x)),
  }));

  const event = events.find(e => String(e.id) === String(form.event_id));
  const locations = useMemo(
    () => [...(event?.locations || [])].sort(sortByLocationType), [event]);

  const detailsMissing = showErrors && !form.details.trim();
  const eventMissing = showErrors && !staff && (!form.event_id
    || (form.event_id === OTHER_EVENT && !form.event_other.trim()));

  let n = 0;
  return (
    <Stack spacing={3}>
      <Question n={++n} title="Who is filling in this form?">
        {staff ? <Fixed value={fixed.name} /> : (
          <TextField label="Your name" fullWidth value={form.submitter_name}
                     onChange={set("submitter_name")} autoComplete="name" />
        )}
      </Question>

      <Question n={++n} title="How can we contact you?"
                hint={staff ? null : "A phone number and/or email address, so we can talk to you about "
                  + "your feedback or ask for more detail — and, for a complaint, tell you how we've "
                  + "dealt with it. If you give an email address we'll confirm we've received it."}>
        {staff ? <Fixed value={fixed.contact} /> : (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField label="Phone" fullWidth value={form.submitter_phone} type="tel"
                       onChange={set("submitter_phone")} autoComplete="tel" />
            <TextField label="Email" fullWidth value={form.submitter_email} type="email"
                       onChange={set("submitter_email")} autoComplete="email" />
          </Stack>
        )}
      </Question>

      <Question n={++n} title="Who is the feedback on behalf of, if it isn't you?"
                hint={staff
                  ? "Leave blank if it's your own. If you're writing down what a participant or "
                    + "member of the public told you, put their name here."
                  : "Leave blank if it's your own feedback. If you're a member of our team writing "
                    + "down what someone told you, put their name here."}>
        <TextField label="Their name" fullWidth value={form.on_behalf_of}
                   onChange={set("on_behalf_of")} />
        {form.on_behalf_of.trim() && (
          <FormControl>
            <FormLabel>Their consent</FormLabel>
            <RadioGroup value={form.consent} onChange={set("consent")}>
              <FormControlLabel value="given" control={<Radio />}
                                label="I have their consent to give this feedback on their behalf" />
              <FormControlLabel value="not_required" control={<Radio />}
                                label="Their consent isn't needed" />
            </RadioGroup>
            {form.consent === "not_required" && (
              <TextField label="Why isn't it needed?" fullWidth multiline minRows={2}
                         value={form.consent_reason} onChange={set("consent_reason")}
                         helperText="For example, they're a child and you're their parent, or they're unable to give it" />
            )}
          </FormControl>
        )}
      </Question>

      <Question n={++n} title="What kind of feedback is it?" hint="Tick all that apply.">
        <FormGroup row>
          {natures.map(x => (
            <FormControlLabel key={x} label={x}
                              control={<Checkbox checked={form.nature.includes(x)}
                                                 onChange={() => toggleNature(x)} />} />
          ))}
        </FormGroup>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary", typography: "body2" }}>
          {natures.map(x => (
            <li key={x}><strong>{x}</strong> {NATURE_HINTS[x]}</li>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary">
          We will aim to respond to {staff ? "Complaints and Speaking Up" : "Complaints"} within
          28 days with how we are dealing with it. We take all feedback into consideration.
          We'll contact you if we need to clarify anything.
        </Typography>
      </Question>

      <Question n={++n} title="Which event is it about?">
        {staff ? <Fixed value={fixed.eventLabel} /> : (
          <>
            <FormControl fullWidth error={eventMissing}>
              <InputLabel id="fb-event">Event</InputLabel>
              <Select labelId="fb-event" label="Event" value={form.event_id}
                      onChange={(e) => setForm(f => ({ ...f, event_id: e.target.value,
                                                       location: "", location_details: "" }))}>
                {events.map(e => <MenuItem key={e.id} value={String(e.id)}>{e.label}</MenuItem>)}
                <MenuItem value={OTHER_EVENT}><em>Other — not listed</em></MenuItem>
              </Select>
            </FormControl>
            {form.event_id === OTHER_EVENT && (
              <TextField label="Which event?" fullWidth value={form.event_other}
                         onChange={set("event_other")} error={eventMissing}
                         helperText={eventMissing ? "Please tell us which event it was" : " "} />
            )}
          </>
        )}
      </Question>

      <Question n={++n} title={staff ? "Where were you?" : "Where at the event, if it matters?"}
                hint={staff ? "Your shift details, from your booking." : null}>
        {staff ? <Fixed value={fixed.shiftDetails} empty="No shift details recorded" /> : (
          <>
            <FormControl fullWidth disabled={!form.event_id}>
              <InputLabel id="fb-location">Location</InputLabel>
              <Select labelId="fb-location" label="Location" value={form.location}
                      onChange={set("location")}>
                <MenuItem value=""><em>Not relevant</em></MenuItem>
                {form.event_id !== OTHER_EVENT && locations.map(l => (
                  <MenuItem key={l.id} value={`loc:${l.id}`}>{locationSummaryLabel(l)}</MenuItem>
                ))}
                {FEEDBACK_LOCATION_CHOICES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            {form.location && (
              <TextField fullWidth multiline minRows={2} value={form.location_details}
                         onChange={set("location_details")}
                         label={form.location === "On Course" ? "Where on the course?"
                           : form.location === "Other" ? "Where?" : "Anything more about where (optional)"} />
            )}
          </>
        )}
      </Question>

      <Question n={++n} title="Your feedback"
                hint="Tell us what happened, what was good, or what we could do better.">
        <TextField label="Details" required fullWidth multiline minRows={6} value={form.details}
                   onChange={set("details")} error={detailsMissing}
                   helperText={detailsMissing ? "Please tell us your feedback" : " "} />
      </Question>
    </Stack>
  );
}
